/**
 * Error handling utilities
 * Provides consistent error handling patterns across the application
 */

import { SUPPORTED_PLATFORMS_LABEL } from './urlValidation';

const PARSE_VALIDATION_PREFIX = /^AWATCH parse validation failed:\s*/i;

/**
 * Shown both when the client-side check rejects a link and when the gateway
 * does, so the user reads the same sentence either way.
 */
export const UNSUPPORTED_PLATFORM_ERROR_TEXT =
  `This link is not from a supported social media platform. Supported platforms: ${SUPPORTED_PLATFORMS_LABEL}.`;

/**
 * User-facing error for contact / alert form submissions.
 * Maps server validation errors (e.g. unsupported URL) to clear messages.
 * @param {Error} error - Thrown by fetchJson / handleResponse
 * @returns {{ title: string, text: string }}
 */
export const getContactFormSubmitError = (error) => {
  const rawMessage = error?.message?.trim() || '';
  const cleanedMessage = rawMessage.replace(PARSE_VALIDATION_PREFIX, '').trim();
  const isClientError = error?.status >= 400 && error?.status < 500;

  if (cleanedMessage.includes('not from a supported social media platform')) {
    return {
      title: 'Please check the form',
      text: UNSUPPORTED_PLATFORM_ERROR_TEXT,
    };
  }

  if (cleanedMessage && isClientError) {
    return {
      title: 'Please check the form',
      text: cleanedMessage,
    };
  }

  if (cleanedMessage && cleanedMessage !== 'Unknown error' && !/^HTTP \d+/.test(cleanedMessage)) {
    return {
      title: 'Could not send',
      text: cleanedMessage,
    };
  }

  return {
    title: 'Could not send',
    text: 'Could not send right now. Please try again.',
  };
};

/**
 * Handle API errors with standardized format
 * @param {Error} error - The error object
 * @param {string} context - Context where error occurred
 * @returns {Object} - Standardized error object
 */
export const handleApiError = (error, context = 'API call') => {
  const errorObj = {
    message: error.message || 'Unknown error',
    context,
    timestamp: new Date().toISOString(),
    stack: error.stack
  };

  // Check for specific error types
  if (error.response) {
    // Server responded with error status
    errorObj.status = error.response.status;
    errorObj.statusText = error.response.statusText;
    errorObj.data = error.response.data;
  } else if (error.request) {
    // Request was made but no response received
    errorObj.type = 'NO_RESPONSE';
    errorObj.message = 'No response from server';
  } else {
    // Error in request setup
    errorObj.type = 'REQUEST_SETUP';
  }

  return errorObj;
};

/**
 * Create error logger for consistent error handling
 * In production, this could be extended to send errors to a logging service
 * @param {string} component - Component name
 * @returns {Object} - Logger functions
 */
export const createErrorLogger = (component) => {
  const prefix = `[${component}]`;
  
  return {
    error: (message, error) => {
      // Only log in development or if explicitly enabled
      if (process.env.NODE_ENV === 'development') {
        console.error(`${prefix} ${message}`, error);
      }
      // In production, you could send to error tracking service (e.g., Sentry)
    },
  };
};
